import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DangerConfirmDialog } from "./danger-confirm-dialog";

interface DangerActionProps {
  actionLabel: string;
  dialogTitle?: string;
  description: React.ReactNode;
  confirmSlug: string;
  pendingLabel?: string;
  auditTrailText?: string;
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  icon?: React.ReactNode;
  /**
   * Controlled open. When provided, the component does not own its own open state —
   * useful for opening the dialog from outside (query-param auto-focus, dropdown menus).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DangerAction({
  actionLabel,
  dialogTitle,
  description,
  confirmSlug,
  pendingLabel,
  auditTrailText,
  onConfirm,
  isPending = false,
  disabled = false,
  disabledReason,
  icon,
  open: controlledOpen,
  onOpenChange,
}: DangerActionProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disabled || isPending}
        title={disabled && disabledReason ? disabledReason : undefined}
        className="border-[color:var(--color-danger)] text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/5"
      >
        {icon ?? <Trash2 className="mr-2 h-4 w-4" aria-hidden />}
        {actionLabel}
      </Button>
      <DangerConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={dialogTitle ?? actionLabel}
        description={description}
        confirmSlug={confirmSlug}
        actionLabel={actionLabel}
        pendingLabel={pendingLabel}
        auditTrailText={auditTrailText}
        onConfirm={onConfirm}
        isPending={isPending}
      />
    </>
  );
}
